import { Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";

/**
 * The subject status a notification is about, as resolved by the server. Typed
 * structurally so every notification query shape (tray, inbox, detail) can pass
 * its own generated type in without a cast.
 */
export type NotificationLiveStatusValue = {
  status: string;
  tone: string;
};

type PaletteKey = "info" | "warning" | "error" | "success";

/**
 * Tone is a semantic signal, not a colour — the design system owns the mapping.
 * `NONE` (and any tone the server adds later that we do not know yet) renders
 * nothing rather than guessing at a colour.
 */
const TONE_PALETTE_KEY: Record<string, PaletteKey> = {
  OK: "info",
  WARNING: "warning",
  CRITICAL: "error",
  RESOLVED: "success",
};

/**
 * Whether {@link NotificationLiveStatus} would render anything for this value.
 * Callers that need a fallback (a resolved notification whose subject has no
 * live status, say) ask here rather than re-deriving the tone rules.
 */
export function hasRenderableLiveStatus(liveStatus?: NotificationLiveStatusValue | null): boolean {
  return Boolean(liveStatus?.status?.trim() && TONE_PALETTE_KEY[liveStatus.tone]);
}

export function NotificationLiveStatus({
  liveStatus,
}: {
  liveStatus?: NotificationLiveStatusValue | null;
}) {
  const status = liveStatus?.status?.trim();
  const paletteKey = liveStatus ? TONE_PALETTE_KEY[liveStatus.tone] : undefined;

  if (!status || !paletteKey) {
    return null;
  }

  return (
    <Chip
      size="small"
      label={status}
      sx={(theme) => ({
        bgcolor: alpha(theme.palette[paletteKey].main, 0.08),
        color: theme.palette[paletteKey].main,
        flexShrink: 0,
        fontWeight: 500,
        maxWidth: 160,
      })}
    />
  );
}
